import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border-2 border-red-200 dark:border-red-900/50 rounded-lg p-4 shadow-sm flex flex-col gap-2 relative overflow-hidden" }
const _hoisted_3 = { class: "absolute right-[-20px] top-[-20px] opacity-10 pointer-events-none" }
const _hoisted_4 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_5 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex flex-col gap-1 pr-6" }
const _hoisted_9 = ["onUpdate:modelValue"]
const _hoisted_10 = { class: "flex flex-col gap-1" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, Fish } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface IshikawaCategory {
  id: string
  name: string
  causesText: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IshikawaEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

const problem = ref('Caída del Sistema')
const categories = ref<IshikawaCategory[]>([
  { id: crypto.randomUUID(), name: 'Infraestructura', causesText: 'Servidor caído, Base de datos saturada' },
  { id: crypto.randomUUID(), name: 'Personal', causesText: 'Falta de capacitación, Error humano' },
  { id: crypto.randomUUID(), name: 'Procesos', causesText: 'Despliegue sin pruebas, Ausencia de monitoreo' }
])

const generateId = () => crypto.randomUUID()

const addCategory = () => {
  categories.value.push({
    id: generateId(),
    name: `Nueva Categoría`,
    causesText: 'Causa 1, Causa 2'
  })
}

const removeCategory = (idx: number) => {
  categories.value.splice(idx, 1)
}

const generateMermaid = () => {
  let code = `ishikawa-beta\n`
  code += `  ${problem.value.trim() || 'Problema Principal'}\n`
  
  categories.value.forEach(cat => {
    if (cat.name.trim()) {
      code += `    ${cat.name.trim()}\n`
      const causes = cat.causesText.split(',').map(c => c.trim()).filter(c => c)
      causes.forEach(cause => {
        code += `      ${cause}\n`
      })
    }
  })
  
  editableCode.value = code
}

const syncToVisual = () => {
  const lines = editableCode.value.split('\n')
  let currentProblem = ''
  const newCategories: IshikawaCategory[] = []
  
  let currentCat: IshikawaCategory | null = null
  
  for (const line of lines) {
    if (line.trim() === 'ishikawa-beta' || !line.trim()) continue
    
    // Analizar la identación para deducir la jerarquía
    const indentMatch = line.match(/^(\s+)/)
    const indentCount = indentMatch ? indentMatch[1].length : 0
    const text = line.trim()
    
    if (indentCount === 2) {
       // Nivel de Problema
       currentProblem = text
    } else if (indentCount === 4) {
       // Nivel de Categoría
       currentCat = { id: generateId(), name: text, causesText: '' }
       newCategories.push(currentCat)
    } else if (indentCount >= 6) {
       // Nivel de Causa (lo agrupamos por comas si hay varios)
       if (currentCat) {
         if (currentCat.causesText) {
            currentCat.causesText += `, ${text}`
         } else {
            currentCat.causesText = text
         }
       }
    }
  }
  
  if (currentProblem) problem.value = currentProblem
  if (newCategories.length > 0) categories.value = newCategories
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([problem, categories], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Diagrama Ishikawa (Espina de Pescado)",
    description: "Identifica las causas raíz de un problema o efecto categorizándolas en ramas principales.",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[3] || (_cache[3] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Fish), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "default",
        onClick: addCategory
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
          _cache[4] || (_cache[4] = _createTextVNode(" Añadir Categoría", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(Fish), { class: "w-32 h-32 text-red-500" })
          ]),
          _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "font-semibold text-lg text-red-600 dark:text-red-400" }, "Problema / Efecto Central", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((problem).value = $event)),
            class: "bg-background border rounded px-3 py-2 text-base font-bold focus:outline-none focus:ring-2 focus:ring-red-500 w-full md:w-2/3",
            placeholder: "Ej. Defecto de Producción"
          }, null, 512), [
            [_vModelText, problem.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[8] || (_cache[8] = _createElementVNode("h3", { class: "font-semibold text-lg border-b pb-2 flex items-center gap-2" }, " Estructura de Causas (Espinas) ", -1)),
          (categories.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, " No hay categorías. El pescado solo tiene cabeza. "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (cat, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: cat.id,
                class: "flex flex-col gap-3 bg-muted/30 border p-3 rounded-md relative group hover:bg-muted/50 transition-colors"
              }, [
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeCategory(idx)),
                  class: "absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-1 rounded opacity-0 group-hover:opacity-100",
                  title: "Eliminar Categoría"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_7),
                _createElementVNode("div", _hoisted_8, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1 text-primary" }, "Categoría (Espina Mayor)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((cat.name) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm font-semibold focus:ring-1 focus:outline-none focus:ring-primary",
                    placeholder: "Ej. Equipamiento"
                  }, null, 8, _hoisted_9), [
                    [_vModelText, cat.name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _cache[7] || (_cache[7] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Causas (Separadas por coma)", -1)),
                  _withDirectives(_createElementVNode("textarea", {
                    "onUpdate:modelValue": ($event: any) => ((cat.causesText) = $event),
                    class: "bg-background border rounded px-2 py-2 text-sm focus:ring-1 focus:outline-none resize-none min-h-[60px]",
                    placeholder: "Ej. Máquina obsoleta, Falta de mantenimiento"
                  }, null, 8, _hoisted_11), [
                    [_vModelText, cat.causesText]
                  ])
                ])
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_12, [
        _cache[10] || (_cache[10] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[9] || (_cache[9] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})